import { useEffect, useMemo, useState } from 'react';
import { Badge, Button, Icon } from '../ui';
import type { BadgeTone } from '../ui';
import AiJobChips from '../ai/AiJobChips';
import type { AiJob } from '../../lib/aiJobs';
import type { HostedSite, HostingAccount, HostingRun, HostingRunStep } from '../../lib/hosting';
import { buildFixPlan, enqueueHostingFix, fetchFixJobs, fixJobFor, fixTargetOf, sendableFixSites, type FixItem, type FixUrgency } from '../../lib/hostingFixPlan';

export type FixPlanContext = {
  account: HostingAccount;
  clientName: string | null;
  user: { id: string; name: string } | null;
  onError: (message: string) => void;
  onQueued: (message: string) => void;
};

// The agent's work on the site is finished; the run's findings predate it.
const FIX_DONE: AiJob['status'][] = ['complete', 'submitted'];

const URGENCY: Record<FixUrgency, { label: string; tone: BadgeTone }> = {
  urgent: { label: 'Urgent', tone: 'down' },
  soon: { label: 'Fix soon', tone: 'warn' },
  later: { label: 'When convenient', tone: 'neutral' },
};

/**
 * "What this means": every finding of a finished run in plain English, with
 * who can fix it. Agent-fixable findings on a site with a repo or a Cloudways
 * app can be sent to the agent queue as one job per site.
 */
export default function FixPlan({ run, steps, sites, ctx }: { run: HostingRun; steps: HostingRunStep[]; sites: HostedSite[]; ctx?: FixPlanContext }) {
  const items = useMemo(() => buildFixPlan(steps, sites), [steps, sites]);
  const [jobs, setJobs] = useState<AiJob[]>([]);
  const [busySite, setBusySite] = useState<string | null>(null);
  const [openWhy, setOpenWhy] = useState<Record<string, boolean>>({});

  useEffect(() => {
    let live = true;
    fetchFixJobs(run.id)
      .then((j) => live && setJobs(j))
      .catch(() => live && setJobs([]));
    return () => {
      live = false;
    };
  }, [run.id]);

  if (run.status !== 'attention' && run.status !== 'failed') return null;
  if (!items.length) return null;

  const sendable = sendableFixSites(steps, sites, items);
  const howItGoes = (site: HostedSite) => {
    const t = fixTargetOf(steps, site);
    return t?.platform === 'cloudways'
      ? 'Worked on the live WordPress site over SSH, after a snapshot; you approve the spec before anything changes.'
      : `One PR against ${t?.repo}; you approve the spec, then review the PR and its deploy preview.`;
  };
  const jobFor = (siteId: string) => fixJobFor(jobs, siteId);

  const send = async (site: HostedSite) => {
    if (!ctx?.user) return;
    setBusySite(site.id);
    try {
      const job = await enqueueHostingFix({ account: ctx.account, clientName: ctx.clientName, run, site, steps, items, createdBy: ctx.user });
      setJobs((j) => [job, ...j]);
      ctx.onQueued('Sent to the agent queue as a spec job');
    } catch (err) {
      ctx.onError(err instanceof Error ? err.message : 'Could not queue the fixes');
    } finally {
      setBusySite(null);
    }
  };

  const agentCount = items.filter((i) => i.owner === 'agent').length;
  const personCount = items.length - agentCount;

  return (
    <div
      style={{
        border: '1px solid var(--border-hairline)',
        borderRadius: 'var(--radius-md)',
        padding: '14px 16px',
        display: 'flex',
        flexDirection: 'column',
        gap: '12px',
        background: 'var(--surface-raised, transparent)',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap' }}>
        <div>
          <div className="t-eyebrow">What this means</div>
          <div style={{ fontSize: 'var(--text-sm)', color: 'var(--text-muted)', marginTop: '2px' }}>
            {plural(items.length, 'thing')} to deal with · {agentCount} an agent can fix · {personCount} for a person
          </div>
        </div>
      </div>

      <ol style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column' }}>
        {items.map((i, n) => (
          <FixRow key={i.key} item={i} first={n === 0} open={Boolean(openWhy[i.key])} onToggle={() => setOpenWhy((o) => ({ ...o, [i.key]: !o[i.key] }))} multiSite={sites.length > 1} />
        ))}
      </ol>

      {ctx && sendable.length ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', borderTop: '1px solid var(--border-hairline)', paddingTop: '12px' }}>
          {sendable.length > 1 ? (
            <div style={{ fontSize: 'var(--text-xs)', color: 'var(--text-faint)' }}>One agent job per site, so each can be approved and shipped on its own.</div>
          ) : null}
          {sendable.map((site) => {
            const job = jobFor(site.id);
            return (
              <div key={site.id} style={{ display: 'flex', alignItems: 'center', gap: '6px 10px', flexWrap: 'wrap' }}>
                {/* Several sites: the host leads each line, so the buttons line up under one another. */}
                {sendable.length > 1 ? (
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', flex: '0 0 auto', minWidth: '150px', fontSize: 'var(--text-sm)', color: 'var(--text-body)' }}>
                    <Icon name="globe" size={12} style={{ color: 'var(--text-faint)' }} />
                    {hostOf(site.url)}
                  </span>
                ) : null}
                {job ? (
                  <>
                    <span style={{ fontSize: 'var(--text-sm)', color: 'var(--text-muted)' }}>
                      {FIX_DONE.includes(job.status) ? 'Fixed by the agent. Run the checklist again to confirm' : 'In the agent queue'}
                    </span>
                    <AiJobChips job={job} />
                  </>
                ) : (
                  <>
                    <Button size="sm" variant="primary" icon="sparkles" onClick={() => send(site)} disabled={!ctx.user || busySite !== null}>
                      {busySite === site.id ? 'Sending…' : `Send ${plural(items.filter((x) => x.site_id === site.id && x.owner === 'agent').length, 'fix', 'fixes')} to the agent queue`}
                    </Button>
                    <span style={{ fontSize: 'var(--text-xs)', color: 'var(--text-faint)' }}>{howItGoes(site)}</span>
                  </>
                )}
              </div>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}

function FixRow({ item, first, open, onToggle, multiSite }: { item: FixItem; first: boolean; open: boolean; onToggle: () => void; multiSite: boolean }) {
  const u = URGENCY[item.urgency];
  return (
    <li style={{ borderTop: first ? 'none' : '1px solid var(--border-hairline)', padding: '10px 0', display: 'flex', flexDirection: 'column', gap: '6px' }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '6px 10px', flexWrap: 'wrap' }}>
        {/* Label column beside the text on wide screens, above it on a phone. */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', flex: '0 0 128px' }}>
          <Badge tone={u.tone}>{u.label}</Badge>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', fontSize: 'var(--text-xs)', color: 'var(--text-faint)' }}>
            <Icon name={item.owner === 'agent' ? 'sparkles' : 'user'} size={11} />
            {item.owner === 'agent' ? 'Agent can fix' : 'Needs a person'}
          </span>
        </div>
        <div style={{ minWidth: 0, flex: '1 1 240px', display: 'flex', flexDirection: 'column', gap: '4px' }}>
          <span style={{ fontSize: 'var(--text-sm)', color: 'var(--text-body)', overflowWrap: 'anywhere' }}>
            {multiSite && item.site ? <span style={{ color: 'var(--text-muted)', fontWeight: 500 }}>{item.site} · </span> : null}
            {item.title}
          </span>
          <span style={{ fontSize: 'var(--text-xs)', color: 'var(--text-muted)', lineHeight: 'var(--leading-normal)', overflowWrap: 'anywhere' }}>{item.action}</span>
          {item.why ? (
            <button
              type="button"
              onClick={onToggle}
              className="hover-underline"
              style={{ alignSelf: 'flex-start', border: 0, padding: 0, background: 'transparent', color: 'var(--text-faint)', fontSize: 'var(--text-xs)', cursor: 'pointer', fontFamily: 'inherit' }}
            >
              {open ? 'Hide why' : 'Why it matters'}
            </button>
          ) : null}
          {open && item.why ? <span style={{ fontSize: 'var(--text-xs)', color: 'var(--text-muted)', lineHeight: 'var(--leading-normal)' }}>{item.why}</span> : null}
        </div>
      </div>
    </li>
  );
}

const hostOf = (url: string) => url.replace(/^https?:\/\//i, '').replace(/\/.*$/, '');
const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;
